import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "@/components/Layout";
import { useSeo } from "@/hooks/useSeo";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { getGuide, setGuideRef, guideClickthrough } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { isGeneral, flagProps } from "@/lib/countries";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    getGuide(slug)
      .then((a) => {
        setArticle(a);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [slug]);

  useSeo({
    title: article ? article.seo_title || article.title : undefined,
    description: article ? article.seo_description || article.excerpt : undefined,
    canonical: article ? `${window.location.origin}/guides/${article.slug}` : undefined,
    image: article ? article.social_image : undefined,
    type: "article",
  });

  if (state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-sand">
        <Loader2 className="h-8 w-8 animate-spin text-clay" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen flex flex-col bg-sand">
        <Header />
        <main className="flex-1 grid place-items-center px-6">
          <div className="text-center max-w-md" data-testid="guide-notfound">
            <h1 className="font-heading text-2xl text-ink">Guide not found</h1>
            <p className="mt-3 text-ink-soft">This article may have been moved or unpublished.</p>
            <button onClick={() => navigate("/guides")} className="mt-6 inline-flex items-center gap-2 text-clay hover:text-clay-hover">
              <ArrowLeft className="h-4 w-4" /> Back to Expert guides
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 sm:px-8 py-10 sm:py-14">
        <Link to="/guides" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-clay" data-testid="back-to-guides">
          <ArrowLeft className="h-4 w-4" /> Expert guides
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mt-6"
        >
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-ink leading-tight" data-testid="guide-title">
            {article.title}
          </h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-ink-faint">
            {!isGeneral(article.category) && flagProps(article.category) && (
              <span className="inline-flex items-center gap-1.5 text-ink-muted" data-testid="article-flag">
                <img {...flagProps(article.category)} width={20} height={15} alt={`${article.category} flag`} className="rounded-[2px] shadow-sm" />
                {article.category}
              </span>
            )}
            <span>{fmtDate(article.created_at)}</span>
          </div>

          <div
            className="article-prose mt-8 text-ink-soft"
            data-testid="guide-body"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.body_html) }}
          />
        </motion.article>

        <div className="mt-12 rounded-2xl border border-clay/30 bg-clay/[0.04] p-8 text-center">
          <h2 className="font-heading text-xl text-ink">Ready to put this into practice?</h2>
          <p className="mt-2 text-ink-soft">Get structured, tutor-style feedback on your own statement.</p>
          <Link
            to="/analyse"
            data-testid="article-cta"
            onClick={() => {
              setGuideRef(article.slug);
              guideClickthrough(article.slug);
            }}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-clay hover:bg-clay-hover text-white px-7 py-4 font-medium transition-colors"
          >
            Get instant feedback on your statement
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
